import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
  type TextareaHTMLAttributes,
} from 'react';
import { Tickets, type TicketSummary, type UserSummary } from '../lib/api';

interface Props extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'onPaste'> {
  value: string;
  onChange: (next: string) => void;
  /** Users available for @-mentioning. Parent is expected to filter out the signed-in user. */
  users: UserSummary[];
  /** User-ids currently mentioned in this comment (parent-owned state). */
  mentions: string[];
  onMentionsChange: (ids: string[]) => void;
  /**
   * Optional callback fired when the user pastes one or more image files into
   * the textarea. When set, image-file paste events are consumed
   * (`preventDefault`) and the parent is expected to handle upload + display.
   * Non-image paste falls through to default text-paste behavior.
   */
  onImagePaste?: (files: File[]) => void;
}

const MAX_MATCHES = 8;

/**
 * Textarea with inline "#"-and-"@"-autocomplete.
 *
 *   * Typing "#" opens a ticket picker (same UX as the existing
 *     TicketLinkTextarea); selecting inserts "#&lt;id&gt;".
 *   * Typing "@" opens a user picker filtered by what's typed after the "@";
 *     selecting inserts "@&lt;Name&gt;" and adds the user-id to the mentions
 *     list the parent passes in.
 *
 * Mentions are validated against the text on every change: if the user
 * deletes a "@Name" they previously inserted, the corresponding user-id is
 * removed from the mentions list so they aren't quietly notified about a
 * comment that no longer mentions them.
 *
 * Arrow keys navigate the popover; Enter / Tab inserts; Escape dismisses;
 * whitespace dismisses without inserting (so plain "@bob" with no picker
 * choice doesn't get treated as a mention).
 */
export const MentionTextarea = forwardRef<HTMLTextAreaElement, Props>(
  function MentionTextarea(
    { value, onChange, users, mentions, onMentionsChange, onImagePaste, ...rest },
    ref,
  ) {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => innerRef.current!);

    // One trigger state machine shared between "#" (ticket) and "@" (user) — only
    // one can be open at a time. `triggerKind` carries which.
    const [triggerKind, setTriggerKind] = useState<'ticket' | 'user' | null>(null);
    const [triggerPos, setTriggerPos] = useState<number | null>(null);
    const [query, setQuery] = useState('');
    const [highlight, setHighlight] = useState(0);
    const [ticketResults, setTicketResults] = useState<TicketSummary[]>([]);
    const [ticketsLoading, setTicketsLoading] = useState(false);

    // In-memory match list for users (no API hit needed; parent already loaded them).
    const userResults = matchUsers(users, query);

    function recomputeTrigger(nextValue: string, caret: number) {
      // Walk back from caret looking for the most recent "#" or "@" with no
      // intervening whitespace. Must be preceded by start-of-string or a
      // word-break so we don't trigger on emails, hex colors, etc.
      let i = caret - 1;
      while (i >= 0) {
        const ch = nextValue[i]!;
        if (ch === '#' || ch === '@') {
          const before = i === 0 ? ' ' : nextValue[i - 1]!;
          if (/\s|[(\[{,;]/.test(before)) {
            setTriggerKind(ch === '#' ? 'ticket' : 'user');
            setTriggerPos(i);
            setQuery(nextValue.slice(i + 1, caret));
            setHighlight(0);
            return;
          }
          break;
        }
        if (/\s/.test(ch)) break;
        i -= 1;
      }
      setTriggerKind(null);
      setTriggerPos(null);
      setQuery('');
      setTicketResults([]);
    }

    function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
      const next = e.target.value;
      onChange(next);
      recomputeTrigger(next, e.target.selectionStart ?? next.length);
      pruneStaleMentions(next);
    }

    function pruneStaleMentions(next: string) {
      // Remove mentions whose "@Name" no longer appears in the text. Avoids
      // silently notifying someone whose @-mention the author deleted.
      if (mentions.length === 0) return;
      const stillPresent = mentions.filter((id) => {
        const u = users.find((x) => x.id === id);
        if (!u) return false;
        return next.includes(`@${u.name}`);
      });
      if (stillPresent.length !== mentions.length) {
        onMentionsChange(stillPresent);
      }
    }

    function activeResults(): { id: string | number; render: () => React.ReactNode }[] {
      if (triggerKind === 'ticket') {
        return ticketResults.map((t) => ({
          id: t.id,
          render: () => (
            <>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 font-mono shrink-0">#{t.id}</span>
                <span className="text-slate-900 truncate">{t.title}</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {t.status.replace('_', ' ')} · {t.priority}
              </div>
            </>
          ),
        }));
      }
      if (triggerKind === 'user') {
        return userResults.map((u) => ({
          id: u.id,
          render: () => (
            <div className="flex items-center gap-2">
              <span className="text-slate-900 truncate">{u.name}</span>
              <span className="text-xs text-slate-500 truncate">{u.email}</span>
            </div>
          ),
        }));
      }
      return [];
    }

    function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
      const results = activeResults();
      if (triggerPos == null || results.length === 0) {
        if (triggerPos != null && e.key === 'Escape') {
          e.preventDefault();
          setTriggerKind(null);
          setTriggerPos(null);
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => Math.min(results.length - 1, h + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => Math.max(0, h - 1));
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const pick = results[highlight];
        if (!pick) return;
        if (triggerKind === 'ticket') insertText(`#${pick.id}`);
        else if (triggerKind === 'user') {
          const u = users.find((x) => x.id === pick.id);
          if (u) insertMention(u);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setTriggerKind(null);
        setTriggerPos(null);
      }
    }

    function insertText(insertion: string) {
      if (triggerPos == null) return;
      const before = value.slice(0, triggerPos);
      const after = value.slice(triggerPos + 1 + query.length);
      const next = `${before}${insertion}${after}`;
      onChange(next);
      closeTrigger();
      restoreCaret(before.length + insertion.length);
    }

    function insertMention(u: UserSummary) {
      const insertion = `@${u.name}`;
      insertText(insertion);
      if (!mentions.includes(u.id)) onMentionsChange([...mentions, u.id]);
    }

    function closeTrigger() {
      setTriggerKind(null);
      setTriggerPos(null);
      setQuery('');
      setTicketResults([]);
      setHighlight(0);
    }

    function restoreCaret(pos: number) {
      setTimeout(() => {
        const ta = innerRef.current;
        if (!ta) return;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      }, 0);
    }

    // Ticket search (only fires while the "#" picker is open).
    useEffect(() => {
      if (triggerKind !== 'ticket' || triggerPos == null) return;
      let cancelled = false;
      setTicketsLoading(true);
      const t = setTimeout(async () => {
        try {
          const params: Record<string, string | number> = { limit: MAX_MATCHES, view: 'all' };
          if (query) params.q = query;
          const r = await Tickets.list(params);
          let items = r.tickets;
          const n = parseInt(query, 10);
          if (Number.isFinite(n) && n > 0 && !items.some((it) => it.id === n)) {
            try {
              const direct = await Tickets.get(n);
              items = [direct.ticket, ...items];
            } catch {
              /* not found */
            }
          }
          if (cancelled) return;
          setTicketResults(items.slice(0, MAX_MATCHES));
          setHighlight(0);
        } finally {
          if (!cancelled) setTicketsLoading(false);
        }
      }, 200);
      return () => {
        cancelled = true;
        clearTimeout(t);
      };
    }, [triggerKind, triggerPos, query]);

    const open = triggerPos != null;
    const results = activeResults();

    return (
      <div className="relative">
        <textarea
          ref={innerRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={(e) => {
            if (!onImagePaste) return;
            const files: File[] = [];
            for (const item of Array.from(e.clipboardData.items)) {
              if (item.kind === 'file' && item.type.startsWith('image/')) {
                const f = item.getAsFile();
                if (f) files.push(f);
              }
            }
            if (files.length > 0) {
              // Consume + stop bubbling so the Attachments tab's window-level
              // paste listener (if it ever runs in the same DOM) doesn't
              // double-handle the same image.
              e.preventDefault();
              e.stopPropagation();
              onImagePaste(files);
            }
          }}
          onBlur={() => {
            // Defer so a click on a result still registers before we close.
            setTimeout(() => closeTrigger(), 150);
          }}
          {...rest}
        />
        {open && (
          <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto">
            <div className="px-3 py-1.5 border-b border-slate-100 text-xs text-slate-500 flex justify-between">
              <span>
                {triggerKind === 'ticket' ? 'Link a ticket — typing ' : 'Mention someone — typing '}
                <code className="font-mono">
                  {triggerKind === 'ticket' ? '#' : '@'}
                  {query || '…'}
                </code>
              </span>
              <span className="text-slate-500">↑↓ navigate · Enter to insert</span>
            </div>
            {triggerKind === 'ticket' && ticketsLoading && ticketResults.length === 0 ? (
              <div className="px-3 py-3 text-sm text-slate-500">Searching…</div>
            ) : results.length === 0 ? (
              <div className="px-3 py-3 text-sm text-slate-500">No matches</div>
            ) : (
              results.map((r, idx) => (
                <button
                  key={String(r.id)}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (triggerKind === 'ticket') insertText(`#${r.id}`);
                    else if (triggerKind === 'user') {
                      const u = users.find((x) => x.id === r.id);
                      if (u) insertMention(u);
                    }
                  }}
                  className={`w-full text-left px-3 py-2 text-sm ${
                    idx === highlight ? 'bg-brand-50' : 'hover:bg-slate-50'
                  }`}
                >
                  {r.render()}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    );
  },
);

function matchUsers(users: UserSummary[], query: string): UserSummary[] {
  if (!query) return users.slice(0, MAX_MATCHES);
  const q = query.toLowerCase();
  // Exact-prefix on name wins, then substring on name, then substring on email.
  const scored = users
    .map((u) => {
      const name = u.name.toLowerCase();
      const email = u.email.toLowerCase();
      let score: number | null = null;
      if (name.startsWith(q)) score = 0;
      else if (name.includes(q)) score = 1;
      else if (email.includes(q)) score = 2;
      return score == null ? null : { u, score };
    })
    .filter((x): x is { u: UserSummary; score: number } => x !== null)
    .sort((a, b) => a.score - b.score || a.u.name.localeCompare(b.u.name));
  return scored.slice(0, MAX_MATCHES).map((x) => x.u);
}
